import React, { SyntheticEvent, useCallback, useState } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { UseFiltersColumnProps } from 'react-table';

import { Theme } from '@app/theme';
import { Input, Checkbox } from '@app/form';

import { useDebouncedSetFilter, useDebouncedHandleChange } from './useDebounce';
import { useTranslation } from 'react-i18next';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    padding: theme.marginBase,
  },
  input: {
    marginBottom: theme.marginBase * 2,
  },
}));

interface Props {
  column: UseFiltersColumnProps<any>;
}

export const EMPTY_VALUES_ONLY = 'EMPTY_VALUES_ONLY';

const emptyValues = [EMPTY_VALUES_ONLY];

export const MinMaxFilter = ({
  column: { filterValue = [], setFilter },
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();

  const [minValue, setMinValue] = useState<number | undefined | false>(
    filterValue[0],
  );
  const [maxValue, setMaxValue] = useState<number | undefined | false>(
    filterValue[1],
  );

  const [isEmptyChecked, setEmpty] = useState<boolean>(
    filterValue[0] === EMPTY_VALUES_ONLY,
  );

  const debouncedSetFilter = useDebouncedSetFilter(setFilter);

  const handleMinChange = useCallback(
    (e: SyntheticEvent<HTMLInputElement>) => {
      const { value } = e.currentTarget;
      const min = value ? Number(value) : undefined;
      setMinValue(min);
      setEmpty(false);
      debouncedSetFilter([min, maxValue]);
    },
    [debouncedSetFilter, maxValue, setEmpty],
  );

  const handleMaxChange = useCallback(
    (e: SyntheticEvent<HTMLInputElement>) => {
      const { value } = e.currentTarget;
      const max = value ? Number(value) : undefined;
      setMaxValue(max);
      setEmpty(false);
      debouncedSetFilter([minValue, max]);
    },
    [debouncedSetFilter, minValue, setEmpty],
  );

  const onEmptyChange = useCallback(
    (e: SyntheticEvent<HTMLInputElement>) => {
      const { checked } = e.currentTarget;
      if (checked) {
        setEmpty(true);
        return debouncedSetFilter(emptyValues);
      }
      if (!checked) {
        setEmpty(false);
        return debouncedSetFilter([]);
      }
    },
    [debouncedSetFilter, setEmpty],
  );
  const debouncedHandleMinChange = useDebouncedHandleChange(handleMinChange);
  const debouncedHandleMaxChange = useDebouncedHandleChange(handleMaxChange);
  const debouncedOnEmptyChange = useDebouncedHandleChange(onEmptyChange);

  return (
    <div className={classes.container}>
      <Input
        small
        noMargin
        value={minValue}
        onChange={debouncedHandleMinChange}
        label={t('participants.list.filters.min')}
        type="number"
        containerClass={classes.input}
      />
      <Input
        small
        noMargin
        value={maxValue}
        onChange={debouncedHandleMaxChange}
        label={t('participants.list.filters.max')}
        type="number"
      />
      <Checkbox
        label={t('participants.list.filters.empty')}
        noMargin
        checked={isEmptyChecked}
        onChange={debouncedOnEmptyChange}
      />
    </div>
  );
};
